import "./WebGallery.css";
import React, { useState, useEffect } from "react";
import { Redirect, useParams } from "react-router-dom";
import { observer } from "mobx-react";
import { useWebGalleryStore } from "../../../hooks";
import { ErrorTypes } from "../../../types";
import { GalleryState } from "../../../stores/WebGalleryStore";
import { IWebGalleryUrlParams } from "../../App";

import Page from "../../Page";
import { GalleryLoader } from "../../partials/GalleryLoader";
import { GalleryRequested } from "../../partials/GalleryRequested";
import { GalleryEmpty } from "../../partials/GalleryEmpty";
import { GalleryGrid } from "../../partials/GalleryGrid";
import { GalleryError } from "../../partials/GalleryError";

const WebGallery: React.FunctionComponent = () => {
  const store = useWebGalleryStore();
  const [isFetching, setIsFetching] = useState(false);
  const [isReFetching, setIsReFetching] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { galleryId } = useParams<IWebGalleryUrlParams>();

  useEffect(() => {
    if (!store.gallery) {
      setIsFetching(true);
      setError(null);
      store
        .fetchGalleryById(galleryId)
        .catch((error) => {
          if (!error.message.includes(ErrorTypes.API_NOT_CONFIGFURED)) {
            console.error(error);
          }
          setError(error);
        })
        .finally(() => {
          setIsFetching(false);
        });
    }

    // refetch after 15s if gallery is in requested state or there are stale assets
    const interval = setInterval(() => {
      if (store.shouldPoll) {
        setIsReFetching(true);
        store
          .fetchGalleryById(galleryId)
          .catch((error) => {
            if (!error.message.includes(ErrorTypes.API_NOT_CONFIGFURED)) {
              console.error(error);
            }
            setError(error);
          })
          .finally(() => {
            setIsReFetching(false);
          });
      }
        stopInterval()
    }, 15000);

    // stop refetching if gallery is in external shared state and there are no stale assets
    const stopInterval = () => {
      if (store.gallery?.state === GalleryState.EXTERNAL_SHARED && !store.staleAssets) {
        clearInterval(interval)
      }
    }
    return () => clearInterval(interval);

  }, [store.api.endpoint, galleryId]);

  if (error) {
    if (error.message.includes(ErrorTypes.UNAUTHORIZED)) {
      return <Redirect to={`/${galleryId}-ExternalUser/authorize`} />;
    } else if (error.message.includes(ErrorTypes.API_NOT_CONFIGFURED)) {
      return <GalleryLoader />;
    } else {
      return (
        <Page>
          <GalleryError />
        </Page>
      );
    }
  }

  // show loading indicator while fetching
  if (isFetching || !store.gallery) {
    return <GalleryLoader />;
  }

  // gallery has specific requested state
  if (!isFetching && !isReFetching && store.gallery?.state === GalleryState.REQUESTED && store?.assets?.length === 0) {
      return (
        <Page>
          <GalleryRequested />
        </Page>
      );
  }

  // gallery has only stale assets, show gallery requested message
  if (!isFetching && !isReFetching && store.staleAssets && store.assets?.length === 0) {
    return (
      <Page>
        <GalleryRequested />
      </Page>
    );
  }

  // show not found or removed message when empty
  if (!isFetching && !isReFetching && store.assets.length === 0) {
    return (
      <Page>
        <GalleryEmpty />
      </Page>
    );
  }
  // on success, default to rendered gallery grid
  return <WebGalleryPage galleryId={galleryId} galleryAssets={store.assets} />;
};

type WebGalleryPageProps = {
  galleryAssets: IAsset[];
  galleryId: string;
};

const WebGalleryPage = ({ galleryId, galleryAssets }: WebGalleryPageProps) => {
  return (
    <Page>
      <GalleryGrid galleryId={galleryId} galleryAssets={galleryAssets} />
    </Page>
  );
};

export default observer(WebGallery);
